// ─────────────────────────────────────────────────────────────────────
// Access Management ▸ Roles tab — Round 3 (2026).
//
// Create, rename, enable/disable, and safe-delete roles. All actions hit
// Round-1 RoleController endpoints, which enforce the real safety rules
// server-side:
//   • duplicate role names rejected (400)
//   • IT-Admin role cannot be renamed/disabled/deleted
//   • a role still assigned to employees cannot be deleted (409) — disable it
//
// This tab surfaces those rules in the UI (confirm modals, disabled
// buttons, clear toasts) but never relies on the FE alone — the server is
// the boundary.
// ─────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from "react";
import {
    Row, Col, Card, CardBody, Input, Button, Spinner, Badge, Table,
    Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { toast } from "react-toastify";
import {
    fetchRoleSummaries, createRole, renameRole, setRoleActive, deleteRole,
} from "../../../api/accessManagement";

export default function RolesTab({ onManagePermissions }) {
    const [roles, setRoles]       = useState([]);   // [{ roleId, roleName, isActive }]
    const [loading, setLoading]   = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [search, setSearch]     = useState("");
    const [working, setWorking]   = useState(false);

    // modal state
    const [createOpen, setCreateOpen] = useState(false);
    const [createName, setCreateName] = useState("");

    const [renameTarget, setRenameTarget] = useState(null); // role object
    const [renameValue,  setRenameValue]  = useState("");

    const [deleteTarget, setDeleteTarget] = useState(null); // role object

    const load = async () => {
        setLoading(true); setLoadError(null);
        try {
            setRoles(await fetchRoleSummaries());   // Round 3: with holder / permission counts
        } catch (e) {
            setLoadError("Could not load roles.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const list = !q ? roles : roles.filter(
            (r) => (r.roleName || "").toLowerCase().includes(q) ||
                   String(r.roleId).includes(q)
        );
        return [...list].sort((a, b) => (a.roleId || 0) - (b.roleId || 0));
    }, [roles, search]);

    // Round 1: delegation-rule refusals arrive as 403 { error, rule } — show
    // the server's explanation verbatim (it names the rule and what to do).
    const apiError = (e, fallback) =>
        toast.error(e?.response?.data?.error || (e?.response?.status === 403 ? "You are not allowed to do that." : fallback));

    // ── create ────────────────────────────────────────────────────────
    const openCreate = () => { setCreateName(""); setCreateOpen(true); };
    const onCreate = async () => {
        const name = createName.trim();
        if (!name) return;
        setWorking(true);
        try {
            const res = await createRole(name);
            toast.success(`Role "${name}" created.`);
            setCreateOpen(false);
            await load();
            // Offer to jump straight to mapping permissions for the new role.
            if (res?.roleId && typeof onManagePermissions === "function") {
                onManagePermissions(res.roleId);
            }
        } catch (e) {
            apiError(e, "Could not create the role.");
        } finally {
            setWorking(false);
        }
    };

    // ── rename ────────────────────────────────────────────────────────
    const openRename = (role) => { setRenameTarget(role); setRenameValue(role.roleName || ""); };
    const onRename = async () => {
        const name = renameValue.trim();
        if (!name || !renameTarget) return;
        setWorking(true);
        try {
            await renameRole(renameTarget.roleId, name);
            toast.success("Role renamed.");
            setRenameTarget(null);
            await load();
        } catch (e) {
            apiError(e, "Could not rename the role.");
        } finally {
            setWorking(false);
        }
    };

    // ── enable / disable ────────────────────────────────────────────────
    const onToggleActive = async (role) => {
        // Round 3 (decision D1): make the semantics explicit before disabling.
        if (role.isActive) {
            const holders = typeof role.totalHolders === "number" ? role.totalHolders : null;
            const msg = `Disable "${role.roleName}"?\n\n` +
                `• It disappears from the role picker and cannot be assigned to anyone new.\n` +
                `• The ${holders == null ? "" : holders + " "}employee(s) who currently hold it KEEP its permissions until they are reassigned.\n\n` +
                `To remove access from current holders, reassign them (Employee Role Assignment) or remove permissions from the role.`;
            if (!window.confirm(msg)) return;
        }
        setWorking(true);
        try {
            await setRoleActive(role.roleId, !role.isActive);
            toast.success(role.isActive
                ? "Role disabled. Current holders keep its permissions until reassigned."
                : "Role enabled.");
            await load();
        } catch (e) {
            apiError(e, "Could not change the role status.");
        } finally {
            setWorking(false);
        }
    };

    // ── delete ────────────────────────────────────────────────────────
    const onDelete = async () => {
        if (!deleteTarget) return;
        setWorking(true);
        try {
            await deleteRole(deleteTarget.roleId);
            toast.success(`Role "${deleteTarget.roleName}" deleted.`);
            setDeleteTarget(null);
            await load();
        } catch (e) {
            // 409 = assigned to employees / IT-Admin — show the server's reason.
            apiError(e, "Could not delete the role.");
        } finally {
            setWorking(false);
        }
    };

    // ── render ────────────────────────────────────────────────────────
    if (loading) {
        return <div className="text-center text-muted py-5"><Spinner size="sm" /> Loading roles…</div>;
    }
    if (loadError) {
        return <div className="alert alert-danger">{loadError}</div>;
    }

    return (
        <>
            <Row className="g-2 align-items-center mb-3">
                <Col md={6}>
                    <Input
                        type="search"
                        bsSize="sm"
                        placeholder="Search roles by name or id…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </Col>
                <Col md={6} className="text-md-end">
                    <Button color="primary" size="sm" onClick={openCreate} disabled={working}>
                        <i className="ri-add-line align-bottom me-1" />
                        New role
                    </Button>
                </Col>
            </Row>

            <Card className="border-0 shadow-sm">
                <CardBody className="p-0">
                    {filtered.length === 0 ? (
                        <div className="text-center text-muted py-4 small">No roles match your search.</div>
                    ) : (
                        <Table responsive borderless className="mb-0 align-middle">
                            <thead>
                                <tr style={{ fontSize: 12, color: "var(--ssw-fg-2, #8a93a8)", textTransform: "uppercase", letterSpacing: 0.4 }}>
                                    <th style={{ width: 70 }}>ID</th>
                                    <th>Role</th>
                                    <th style={{ width: 110 }}>Status</th>
                                    <th style={{ width: 130 }} title="Employees holding the role: active / total">Employees</th>
                                    <th style={{ width: 120 }} title="Permissions granted by the role">Permissions</th>
                                    <th style={{ width: 320 }} className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((r) => {
                                    // The IT Admin role is the unconditional super-user. It must
                                    // never be renamed (its name backs the [Authorize(Roles="IT Admin")]
                                    // gate), disabled, or deleted, and its permissions are irrelevant
                                    // (it bypasses every gate). The backend already blocks these, but
                                    // we also hide the actions so they're not even clickable.
                                    // Round 1: the system role is flagged by the API (isProtected,
                                    // resolved server-side from configuration) — no name matching.
                                    const isItAdminRole = r.isProtected === true;
                                    return (
                                    <tr key={r.roleId} style={{ borderTop: "1px solid var(--ssw-line, #eef0f4)" }}>
                                        <td className="text-muted">{r.roleId}</td>
                                        <td className="fw-medium">
                                            {r.roleName}
                                            {isItAdminRole && (
                                                <i className="ri-shield-keyhole-line text-primary ms-2"
                                                   title="System super-user role" />
                                            )}
                                        </td>
                                        <td>
                                            <Badge color={r.isActive ? "success" : "secondary"} pill style={{ fontWeight: 500 }}
                                                   title={r.isActive ? undefined : "Disabled: hidden from the picker and refused for new assignments; current holders KEEP its permissions until reassigned (decision D1)."}>
                                                {r.isActive ? "Active" : "Disabled"}
                                            </Badge>
                                        </td>
                                        <td>
                                            {typeof r.totalHolders === "number" ? (
                                                <span title={`${r.activeHolders} active of ${r.totalHolders} employees`}>
                                                    {r.activeHolders}<span className="text-muted"> / {r.totalHolders}</span>
                                                </span>
                                            ) : <span className="text-muted">—</span>}
                                        </td>
                                        <td>
                                            {typeof r.permissionCount === "number" ? (
                                                <span>
                                                    {r.permissionCount}
                                                    {r.groupCount > 1 && (
                                                        <i className="ri-error-warning-line text-warning ms-1"
                                                           title={`This role's ROLE:${r.roleId} key maps to ${r.groupCount} permission groups — see the Role Permissions tab.`} />
                                                    )}
                                                </span>
                                            ) : <span className="text-muted">—</span>}
                                        </td>
                                        <td className="text-end">
                                            {isItAdminRole ? (
                                                <span className="text-muted small fst-italic" title="The IT Admin role has full access and cannot be edited, disabled, or deleted.">
                                                    <i className="ri-lock-2-line align-bottom me-1" />
                                                    Protected — full access
                                                </span>
                                            ) : (
                                                <>
                                                    <Button color="link" size="sm" className="p-1 me-1"
                                                        disabled={working}
                                                        onClick={() => onManagePermissions && onManagePermissions(r.roleId)}
                                                        title="Manage this role's permissions/modules">
                                                        <i className="ri-key-2-line align-bottom me-1" />Permissions
                                                    </Button>
                                                    <Button color="link" size="sm" className="p-1 me-1"
                                                        disabled={working}
                                                        onClick={() => openRename(r)}
                                                        title="Rename role">
                                                        <i className="ri-edit-line" />
                                                    </Button>
                                                    <Button color="link" size="sm" className="p-1 me-1"
                                                        disabled={working}
                                                        onClick={() => onToggleActive(r)}
                                                        title={r.isActive ? "Disable role" : "Enable role"}>
                                                        <i className={r.isActive ? "ri-pause-circle-line" : "ri-play-circle-line"} />
                                                    </Button>
                                                    <Button color="link" size="sm" className="p-1 text-danger"
                                                        disabled={working || (r.totalHolders > 0)}
                                                        onClick={() => setDeleteTarget(r)}
                                                        title={r.totalHolders > 0
                                                            ? `Cannot delete: ${r.totalHolders} employee(s) still hold this role. Reassign them or disable the role.`
                                                            : "Delete role"}>
                                                        <i className="ri-delete-bin-line" />
                                                    </Button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            {/* Create modal */}
            <Modal isOpen={createOpen} toggle={() => !working && setCreateOpen(false)}>
                <ModalHeader toggle={() => !working && setCreateOpen(false)}>New role</ModalHeader>
                <ModalBody>
                    <p className="text-muted small mb-2">
                        Give the role a name (e.g. <strong>Shipping</strong>). After it's created you can
                        assign which modules and permissions it grants.
                    </p>
                    <Input
                        autoFocus
                        placeholder="Role name"
                        value={createName}
                        onChange={(e) => setCreateName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") onCreate(); }}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setCreateOpen(false)} disabled={working}>Cancel</Button>
                    <Button color="primary" onClick={onCreate} disabled={working || !createName.trim()}>
                        {working ? <><Spinner size="sm" /> Creating…</> : "Create role"}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Rename modal */}
            <Modal isOpen={!!renameTarget} toggle={() => !working && setRenameTarget(null)}>
                <ModalHeader toggle={() => !working && setRenameTarget(null)}>Rename role</ModalHeader>
                <ModalBody>
                    <Input
                        autoFocus
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") onRename(); }}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setRenameTarget(null)} disabled={working}>Cancel</Button>
                    <Button color="primary" onClick={onRename} disabled={working || !renameValue.trim()}>
                        {working ? <><Spinner size="sm" /> Saving…</> : "Save"}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Delete confirm */}
            <Modal isOpen={!!deleteTarget} toggle={() => !working && setDeleteTarget(null)}>
                <ModalHeader toggle={() => !working && setDeleteTarget(null)}>Delete role</ModalHeader>
                <ModalBody>
                    <p className="mb-2">
                        Delete the role <strong>{deleteTarget?.roleName}</strong>
                        {typeof deleteTarget?.permissionCount === "number" && (
                            <span className="text-muted"> (grants {deleteTarget.permissionCount} permission{deleteTarget.permissionCount === 1 ? "" : "s"})</span>
                        )}?
                    </p>
                    <p className="text-muted small mb-0">
                        This is blocked if any employee is still assigned to it — reassign or disable
                        the role instead. The IT Admin role can never be deleted.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setDeleteTarget(null)} disabled={working}>Cancel</Button>
                    <Button color="danger" onClick={onDelete} disabled={working}>
                        {working ? <><Spinner size="sm" /> Deleting…</> : "Delete"}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
}
